import './ReferClaim.css'
import { Button, FormControl, MenuItem, Select, TextField } from '@mui/material'
import React from 'react'
import DedicatedImage from 'src/assets/PolicyDetails_Images/DedicatedImage.svg'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_INSURANCE_TYPES,
  RAISE_REQUEST,
} from 'src/services/individualService'
import GraphQlErrors from 'src/components/common/GraphQlErrors'
import { getInsuranceTypesList } from 'src/utils/commonMethods'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { NAME_REGEX, PHONE_REGEX } from 'src/utils/constants/BuyPolicyMotor'
import {
  CONTACT_FORM_ERROR_MESSAGES,
  DashboardEmailsEnums,
} from 'src/utils/constants/ValueAddedServicesData'
import {
  getFormControlErrorText,
  isFormControlError,
} from 'src/services/formHelperService'
import { showNotificationMessage } from 'src/actions'
import { NOTIFICATION_TYPES } from 'src/utils/constants/common'
import LoadingModal from 'src/components/common/LoadingModal'

import { makeStyles } from '@material-ui/core'
import {
  CLAIM_REQUEST_RAISED,
  NOTIFICATION_SETTINGS_MSG,
} from 'src/utils/constants/Individual'

const ReferClaim = () => {
  const { loggedInUserInfo } = useSelector((state) => {
    return {
      loggedInUserInfo: state?.userInfo,
    }
  })
  const dispatch = useDispatch()
  const classes = useStyles()

  const initialValues = {
    insuranceType: '',
    name: '',
    phone: '',
    query: '',
  }

  const contactForm = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object({
      name: Yup.string()
        .required(CONTACT_FORM_ERROR_MESSAGES?.NAME_REQUIRED)
        .matches(NAME_REGEX, CONTACT_FORM_ERROR_MESSAGES?.VALID_NAME),
      phone: Yup.string()
        .required(CONTACT_FORM_ERROR_MESSAGES?.PHONE_REQUIRED)
        .matches(PHONE_REGEX, CONTACT_FORM_ERROR_MESSAGES?.VALID_PHONE)
        .max(10, CONTACT_FORM_ERROR_MESSAGES?.VALID_PHONE)
        .min(10, CONTACT_FORM_ERROR_MESSAGES?.VALID_PHONE),
      insuranceType: Yup.string().required(
        CONTACT_FORM_ERROR_MESSAGES?.INSURANCE_TYPE_REQUIRED
      ),
      query: Yup.string().required(CONTACT_FORM_ERROR_MESSAGES?.QUERY_REQUIRED),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true)
      handleSubmit(values)
    },
    enableReinitialize: true,
  })

  const handleSubmit = (values) => {
    raiseRequest({
      variables: {
        emailsDetails: {
          typeOfInsurance: values?.insuranceType,
          emailType: DashboardEmailsEnums?.CLAIM_REQUEST,
          name: values?.name,
          phoneNumber: values?.phone,
          email: loggedInUserInfo?.userInfo?.userData?.emailId,
          userQuery: values?.query,
        },
      },
    })
  }

  const [raiseRequest, { loading }] = useMutation(RAISE_REQUEST(), {
    onCompleted: () => {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES.SUCCESS,
          message: CLAIM_REQUEST_RAISED,
        })
      )
      contactForm.resetForm({
        ...initialValues,
      })
    },
    onError: () => {
      dispatch(
        showNotificationMessage({
          type: NOTIFICATION_TYPES.ERROR,
          message:
            NOTIFICATION_SETTINGS_MSG?.ENCOUNTERED_ERROR_ON_REQUEST_RAISED,
        })
      )
    },
  })

  const {
    loading: insuranceTypesAggregateLoading,
    error: insuranceTypesAggregateError,
    data,
  } = useQuery(GET_INSURANCE_TYPES(), {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })

  if (insuranceTypesAggregateError)
    return (
      <>
        <GraphQlErrors
          error={insuranceTypesAggregateError}
          routeValue={'back'}
        />
      </>
    )

  const insuranceTypesList = getInsuranceTypesList(
    data?.getMasterData?.response?.[0]?.data
  )

  return (
    <>
      <LoadingModal loading={insuranceTypesAggregateLoading || loading} />
      <div className={'refer-claim-container'}>
        <div className={'refer-claim-heading-container'}>
          <span className={'refer-claim-heading'}>
            Goodbye queues and forms!
          </span>
          <span className={'refer-claim-sub-heading'}>
            Refer your friends or family seeking claim advice too!
          </span>
        </div>
        <form className={'refer-claim-form'}>
          <div className={'refer-claim-content'}>
            <div className={'refer-claim-form-container'}>
              <FormControl fullWidth size='small'>
                <Select
                  id='insuranceType'
                  name='insuranceType'
                  placeholder='Insurance Type'
                  displayEmpty
                  defaultValue=''
                  value={contactForm.values.insuranceType}
                  onChange={contactForm.handleChange}
                  onBlur={contactForm.handleBlur}
                  error={isFormControlError(contactForm, 'insuranceType')}
                  helperText={getFormControlErrorText(
                    contactForm,
                    'insuranceType'
                  )}
                >
                  <MenuItem value={''} className={'default-menu-item'} disabled>
                    Insurance Type
                  </MenuItem>
                  {insuranceTypesList?.map((subType) => (
                    <MenuItem key={subType?.id} value={subType}>
                      {subType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size='small'>
                <TextField
                  id='name'
                  name='name'
                  type='text'
                  placeholder='Name'
                  value={contactForm.values.name}
                  onChange={contactForm.handleChange}
                  onBlur={contactForm.handleBlur}
                  error={isFormControlError(contactForm, 'name')}
                  helperText={getFormControlErrorText(contactForm, 'name')}
                  required
                  className={classes.nameField}
                />
              </FormControl>

              <FormControl fullWidth size='small'>
                <TextField
                  id='phone'
                  name='phone'
                  type='number'
                  placeholder='Phone'
                  value={contactForm.values.phone}
                  onChange={(event) => {
                    event.target.value = Math.max(
                      0,
                      parseInt(event?.target?.value)
                    )
                      ?.toString()
                      ?.slice(0, 10)
                    contactForm.setFieldValue(
                      'phone',
                      event?.target?.value,
                      false
                    )
                  }}
                  onBlur={contactForm.handleBlur}
                  error={isFormControlError(contactForm, 'phone')}
                  helperText={getFormControlErrorText(contactForm, 'phone')}
                  required
                  className={classes.nameField}
                />
              </FormControl>
            </div>
            <div className={'refer-claim-image-container'}>
              <img
                src={DedicatedImage}
                alt='dedicatedImage'
                className={'refer-claim-image'}
              />
            </div>
          </div>
          <FormControl fullWidth size='small'>
            <TextField
              id='query'
              name='query'
              type='text'
              placeholder='Enter your query here...'
              value={contactForm.values.query}
              onChange={contactForm.handleChange}
              onBlur={contactForm.handleBlur}
              error={isFormControlError(contactForm, 'query')}
              helperText={getFormControlErrorText(contactForm, 'query')}
              required
              multiline
              rows={3}
            />
          </FormControl>
        </form>
        <div className={'refer-claim-actions-container'}>
          <Button
            variant='outlined'
            onClick={() => {
              contactForm.submitForm()
            }}
            className={'refer-claim-submit-button'}
          >
            Submit
          </Button>
        </div>
      </div>
    </>
  )
}

export default ReferClaim

const useStyles = makeStyles({
  nameField: {
    '&  .MuiOutlinedInput-input': {
      height: '0.7rem',
    },
  },
})
